import React from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import "../../../auth-modern.css";

const TermsOfService = () => (
  <>
    <Helmet>
      <title>Terms of Service - Planit</title>
    </Helmet>

    <div className="auth-modern-wrapper" style={{ alignItems: "center", justifyContent: "flex-start", padding: "40px 20px 60px" }}>
      <div style={{ width: "100%", maxWidth: "860px" }}>
        <div style={{ textAlign: "center", marginBottom: "32px" }}>
          <Link to="/">
            <img src="/assets/images/logo-dark.png" alt="Planit" height={32} />
          </Link>
        </div>

        <div style={{
          background: "#fff",
          borderRadius: "12px",
          padding: "48px 56px",
          boxShadow: "0 2px 20px rgba(0,0,0,0.07)",
          fontFamily: "'Inter', 'Roboto', sans-serif",
          color: "#1D1D1D",
          lineHeight: "1.75",
          fontSize: "15px",
        }}>
          <h1 style={{ fontSize: "26px", fontWeight: 600, marginBottom: "4px" }}>Terms of Service</h1>
          <p style={{ color: "#74788d", marginBottom: "36px", fontSize: "14px" }}>Effective date: 12 October 2026</p>

          <Section title="1. About these terms">
            <p>These terms govern the use of Planit, Websouls' HR and project management platform, through its iOS and Android apps and web application (the "Service"). Planit is a product of Websouls, with its registered office at:</p>
            <address style={{ fontStyle: "normal", paddingLeft: "16px", borderLeft: "3px solid #00DFAB", marginBottom: "12px" }}>
              Office # 22, 2nd Office Floor, IT Tower, Behind Hafeez Center, 73-E-1 Hali Road, Gulberg III, Lahore, Pakistan
            </address>
            <p>These terms apply to two groups:</p>
            <ul>
              <li><strong>Customers:</strong> organisations that subscribe to Planit. By subscribing, the Customer agrees to all of these terms.</li>
              <li><strong>Users:</strong> employees and team members given access by a Customer. By signing in, a User agrees to the sections that apply to Users (sections 3, 5, 8, 11, 12 and 14).</li>
            </ul>
            <p>If a Customer has a signed agreement or order form with Websouls, that document takes priority over these terms where they conflict. Our <Link to="/privacy-policy" style={linkStyle}>Privacy Policy</Link> explains how we handle personal information.</p>
          </Section>

          <Section title="2. Definitions">
            <table style={tableStyle}>
              <thead>
                <tr>
                  <Th>Term</Th>
                  <Th>Meaning</Th>
                </tr>
              </thead>
              <tbody>
                <tr><Td>Customer</Td><Td>The organisation that subscribes to Planit</Td></tr>
                <tr><Td>User</Td><Td>A person the Customer allows to use Planit, such as an employee or contractor</Td></tr>
                <tr><Td>Administrator</Td><Td>A User the Customer authorises to manage its Planit account, Users and settings</Td></tr>
                <tr><Td>Customer Data</Td><Td>All information the Customer or its Users put into Planit, including employee and HR records</Td></tr>
                <tr><Td>Order Form</Td><Td>The quote, order, invoice or agreement in which the Customer's plan, number of Users and fees are set out</Td></tr>
                <tr><Td>Service</Td><Td>The Planit apps, web application and related support</Td></tr>
              </tbody>
            </table>
          </Section>

          <Section title="3. Accounts and access">
            <p>There is no public sign-up for Planit. User accounts are created by the Customer's Administrators, who control each User's role and permissions.</p>
            <ul>
              <li>Users must keep their sign-in details confidential and must not share their account.</li>
              <li>Users must tell their Administrator promptly if they suspect someone else has accessed their account.</li>
              <li>The Customer is responsible for everything done under its Users' accounts, and for removing access when someone leaves the organisation.</li>
              <li>Planit is for use by adults (18 or over) in a work context.</li>
            </ul>
          </Section>

          <Section title="4. Fees and billing">
            <p>The Customer's plan, fees, billing cycle and payment terms are set out in its Order Form. No purchases or payments are made inside the Planit mobile apps, and Users are never charged for using Planit.</p>
            <p>If fees are not paid when due, Websouls may suspend the Service after giving the Customer written notice and a reasonable time to pay (see section 10).</p>
          </Section>

          <Section title="5. Acceptable use">
            <p>Customers and Users must not:</p>
            <ul>
              <li>Break any law, or use Planit to harass, discriminate against or harm anyone</li>
              <li>Upload content they have no right to share, or content that is unlawful, offensive or contains malware</li>
              <li>Try to access accounts, data or systems they are not authorised to access</li>
              <li>Test, probe or attack Planit's security without Websouls' written permission</li>
              <li>Copy, resell, reverse-engineer or build a competing product from Planit, except where the law allows</li>
              <li>Overload or disrupt the Service, including through automated scraping</li>
            </ul>
            <p>Websouls may remove content or suspend access that breaks these rules, and will tell the Customer where it can.</p>
          </Section>

          <Section title="6. Customer Data">
            <p>The Customer owns its Customer Data. Websouls uses it only to provide, secure and support the Service for that Customer, and does not sell it or use it for advertising.</p>
            <p>The Customer is responsible for:</p>
            <ul>
              <li>Having a lawful basis to put employee and HR information into Planit</li>
              <li>Telling its employees how their information is used, including any monitoring such as attendance or location features</li>
              <li>The accuracy of its Customer Data</li>
              <li>Configuring Users' roles and permissions appropriately</li>
              <li>Exporting any Customer Data it wants to keep before cancelling, because it is deleted on cancellation (see section 10)</li>
            </ul>
          </Section>

          <Section title="7. Data processing">
            <p>For personal information in Customer Data, the Customer is the controller and Websouls is the processor. Where data protection law requires it, Websouls will:</p>
            <ol>
              <li>Process personal information only on the Customer's documented instructions, which are these terms, the Order Form and the Customer's use of the Service</li>
              <li>Ensure staff with access are bound by confidentiality</li>
              <li>Apply appropriate security measures to protect the data</li>
              <li>Use sub-processors only under written terms with appropriate protections, and tell the Customer in advance of any new sub-processor (Websouls currently uses Google Firebase only to deliver push notifications)</li>
              <li>Help the Customer respond to requests from individuals exercising their rights</li>
              <li>Notify the Customer without undue delay after becoming aware of a personal data breach</li>
              <li>Delete Customer Data when the subscription ends, as set out in section 10</li>
              <li>Provide information reasonably needed to show compliance with these obligations</li>
              <li>Transfer personal information across borders only with the safeguards required by applicable law</li>
            </ol>
            <p>A Customer that needs a separate, signed data processing agreement can request one at <a href="mailto:support@websouls.com" style={linkStyle}>support@websouls.com</a>.</p>
          </Section>

          <Section title="8. Confidentiality, intellectual property and app licence">
            <p><strong>Confidentiality.</strong> Each party will keep the other's non-public business information confidential and use it only for the purposes of these terms, except where disclosure is required by law.</p>
            <p><strong>Websouls' rights.</strong> Websouls owns Planit, including its software, design, trademarks and documentation. These terms do not transfer any of those rights.</p>
            <p><strong>App licence.</strong> Websouls grants each User a personal, non-exclusive, non-transferable, revocable licence to install and use the Planit app on devices they own or control, only for the Customer's work and only while the Customer's subscription is active.</p>
            <p><strong>Feedback.</strong> If a Customer or User suggests improvements, Websouls may use them freely without any obligation.</p>
          </Section>

          <Section title="9. Availability, support and changes">
            <p>Websouls aims to keep Planit available at all times, apart from planned maintenance, which we will try to schedule outside normal working hours and announce in advance where possible. Any uptime commitment or service credits apply only if set out in the Customer's Order Form.</p>
            <p>Support is provided to Customers' Administrators through <a href="mailto:support@websouls.com" style={linkStyle}>support@websouls.com</a>. Users should contact their Administrator first.</p>
            <p>We may improve, change or remove features. We will give Customers reasonable notice before removing a feature that materially reduces the Service they pay for.</p>
          </Section>

          <Section title="10. Suspension and termination">
            <p><strong>Suspension.</strong> Websouls may suspend access, in whole or for specific Users, if fees remain unpaid after notice, if these terms are seriously breached, or if it is needed to protect the Service or other Customers. We will restore access once the issue is resolved.</p>
            <p><strong>Cancellation.</strong> A Customer may cancel as set out in its Order Form. Either party may end the subscription if the other seriously breaches these terms and does not fix the breach within 30 days of written notice.</p>
            <p><strong>What happens on cancellation.</strong></p>
            <ul>
              <li>Access for all of the Customer's Users ends.</li>
              <li>All Customer Data is deleted from Websouls' live systems immediately, and from backups within 30 days. Deleted data cannot be recovered.</li>
              <li>Fees already paid are not refunded, unless the Order Form says otherwise.</li>
            </ul>
            <p>Sections that by their nature should continue (including confidentiality, intellectual property, liability and governing law) survive the end of the subscription.</p>
          </Section>

          <Section title="11. Warranties and disclaimers">
            <p>Websouls will provide the Service with reasonable skill and care. Apart from that, and to the extent the law allows, Planit is provided "as is" and "as available", without other warranties of any kind.</p>
            <p>Planit is a tool to support HR and project management. It does not give legal, tax, payroll or employment advice. Customers remain responsible for their own HR decisions and for complying with employment laws.</p>
            <p>Nothing in these terms limits any rights that cannot be limited by law, including consumer rights where they apply.</p>
          </Section>

          <Section title="12. Limitation of liability and indemnity">
            <p><strong>Limits.</strong> To the extent the law allows:</p>
            <ul>
              <li>Neither party is liable for indirect or consequential loss, or for loss of profits, revenue, business or goodwill.</li>
              <li>Websouls' total liability under these terms in any 12-month period is limited to the fees the Customer paid for the Service in that period.</li>
              <li>Websouls has no liability to Users individually beyond what the law requires; Users' rights in relation to the Service run through their organisation.</li>
            </ul>
            <p><strong>Not limited.</strong> Nothing in these terms limits liability for death or personal injury caused by negligence, for fraud, or for anything else that cannot legally be limited.</p>
            <p><strong>Customer indemnity.</strong> The Customer will cover Websouls' reasonable losses from claims caused by its Customer Data, or by the Customer or its Users breaking these terms or the law.</p>
          </Section>

          <Section title="13. Governing law and disputes">
            <p>These terms are governed by the laws of Pakistan.</p>
            <p>The courts at Lahore, Pakistan, have exclusive jurisdiction over any dispute arising from them.</p>
            <p>Before going to court, both parties will first try in good faith to resolve any dispute through discussion between senior representatives for at least 30 days.</p>
          </Section>

          <Section title="14. App store terms">
            <p><strong>Apple App Store.</strong> If you downloaded Planit from the Apple App Store:</p>
            <ul>
              <li>These terms are between you and Websouls, not Apple. Websouls, not Apple, is solely responsible for the app and its content.</li>
              <li>Your licence to use the app is limited to Apple-branded devices you own or control, as permitted by the App Store's Usage Rules.</li>
              <li>Websouls is solely responsible for maintenance and support of the app. Apple has no obligation to provide any.</li>
              <li>If the app fails to meet any applicable warranty, you may notify Apple, which may refund the purchase price (if any). To the extent the law allows, Apple has no other warranty obligation for the app.</li>
              <li>Websouls, not Apple, is responsible for handling any claims relating to the app, including product liability claims, claims that it fails to meet legal or regulatory requirements, and consumer protection or privacy claims.</li>
              <li>Websouls, not Apple, is responsible for investigating, defending and settling any claim that the app infringes someone's intellectual property rights.</li>
              <li>You confirm that you are not located in a country subject to a US government embargo or designated as supporting terrorism, and are not on any US government list of prohibited or restricted parties.</li>
              <li>Questions, complaints or claims about the app should be sent to Websouls using the details in section 15.</li>
              <li>You must also comply with any third-party terms that apply when using the app, such as your mobile network's terms.</li>
              <li>Apple and its subsidiaries are third-party beneficiaries of these terms and may enforce them against you.</li>
            </ul>
            <p><strong>Google Play.</strong> If you downloaded Planit from Google Play, these terms are between you and Websouls, not Google. Your use of Google Play is also subject to Google's own terms.</p>
          </Section>

          <Section title="15. Changes, general terms and contact">
            <p><strong>Changes to these terms.</strong> We may update these terms. We will post the new version with a new effective date and give Customers at least 30 days' notice of material changes. Continuing to use Planit after that date means accepting the updated terms.</p>
            <p><strong>General.</strong> If any part of these terms is found unenforceable, the rest remains in effect. A failure to enforce a right is not a waiver of it. Neither party is liable for delays caused by events beyond its reasonable control. A Customer may not transfer its subscription without Websouls' written consent.</p>
            <p><strong>Contact.</strong></p>
            <ul>
              <li>Email: <a href="mailto:support@websouls.com" style={linkStyle}>support@websouls.com</a></li>
              <li>Address: Websouls, Office # 22, 2nd Office Floor, IT Tower, Behind Hafeez Center, 73-E-1 Hali Road, Gulberg III, Lahore, Pakistan</li>
            </ul>
          </Section>

          <p style={{ marginTop: "48px", paddingTop: "24px", borderTop: "1px solid #eee", color: "#74788d", fontSize: "13px", textAlign: "center" }}>
            © {new Date().getFullYear()} Planit. Developed by <a href="https://websouls.com/" target="_blank" rel="noopener noreferrer" style={linkStyle}>WebSouls</a>
            &nbsp;·&nbsp;
            <Link to="/privacy-policy" style={linkStyle}>Privacy Policy</Link>
          </p>
        </div>
      </div>
    </div>
  </>
);

const Section = ({ title, children }) => (
  <div style={{ marginBottom: "32px" }}>
    <h2 style={{ fontSize: "17px", fontWeight: 600, color: "#1D1D1D", marginBottom: "12px", paddingBottom: "8px", borderBottom: "2px solid #00DFAB" }}>{title}</h2>
    {children}
  </div>
);

const tableStyle = {
  width: "100%",
  borderCollapse: "collapse",
  marginBottom: "16px",
  fontSize: "14px",
};

const Th = ({ children }) => (
  <th style={{ background: "#f5fdf9", border: "1px solid #e0f5ef", padding: "10px 14px", textAlign: "left", fontWeight: 600, color: "#1D1D1D" }}>{children}</th>
);

const Td = ({ children }) => (
  <td style={{ border: "1px solid #e8e8e8", padding: "10px 14px", verticalAlign: "top" }}>{children}</td>
);

const linkStyle = { color: "#00DFAB", textDecoration: "none" };

export default TermsOfService;
